import { profiles } from '../core/profiles.js';
import { storage } from '../core/storage.js';
import { device } from '../core/device.js';
import { account } from '../core/account.js';
import { firstScreen } from './start.js';
import { t } from '../core/i18n.js';
import { escapeHtml } from '../core/utils.js';

/* Bienvenida (recorrido de primera ejecución, solo en las apps): cinco pantallas que cuentan qué es Filoteca y llevan a
   «Añadir proveedor». Sale cuando aún no hay ninguna fuente; «Saltar» o «Entrar» la dan por vista. Desde Ajustes se
   puede volver a ver (entonces al terminar se vuelve atrás). Con mando: ← → cambian de pantalla, OK en el botón sigue. */

export const WELCOME_STEPS = 5;
const DONE_KEY = 'welcomeDone';

export function welcomeDone() { return !!storage.get(DONE_KEY); }
export function markWelcomeDone(done = true) { if (done) storage.set(DONE_KEY, true); else storage.remove(DONE_KEY); }

/**
 * ¿Toca enseñar la bienvenida al arrancar? Función pura para poder probarla.
 * Apps: la primera vez, sin ninguna fuente. Web: además solo entrando por la portada (`entryHome`), sin enlaces a fichas ni parámetros.
 */
export function shouldShowWelcome({ standalone = true, needsLogin = false, needsAccount = false, wantsPair = false, providers = 0, done = false, entryHome = true }) {
    if (done || wantsPair || providers > 0 || !needsLogin) return false;
    return standalone ? !needsAccount : !!entryHome;
}

/* Fondos: los que lleva la app (img/welcome-N.jpg) salen al instante; si el servidor tiene otros en downloads/welcome/N.jpg
   (Alfredo los sustituye ahí sin recompilar), se cambian en cuanto se descargan. */
// En las apps el servidor es el de las actualizaciones (lo fija la compilación); en la web, el mismo sitio.
const BASE = (typeof __UPDATE_URL__ !== 'undefined' ? String(__UPDATE_URL__) : '').replace(/\/+$/, '');
// Dos recortes de cada maqueta: apaisado (tele, ordenador: la escena con el televisor a la derecha y el texto a la izquierda) y
// vertical «-p» (móvil: la tele arriba y el texto debajo).
const portrait = () => typeof matchMedia === 'function' && !matchMedia('(min-width: 900px) and (orientation: landscape)').matches;
const variant = () => (portrait() ? '-p' : '');
const remoteBg = (n) => BASE + '/downloads/welcome/' + n + variant() + '.jpg';
const resolved = new Map(); // «n-p» / «n» → url que se ve de verdad (la del servidor si cargó)
function preloadRemote(n, onReady) {
    const url = remoteBg(n);
    const key = n + variant();
    if (!url || resolved.has(key) || typeof Image === 'undefined') return;
    resolved.set(key, null);
    const img = new Image();
    img.onload = () => { resolved.set(key, url); onReady(n, url); };
    img.onerror = () => { resolved.delete(key); };
    img.src = url;
}
export function bgFor(n) {
    return resolved.get(n + variant()) || ('img/' + STEPS[n - 1].bg.replace('.jpg', variant() + '.jpg'));
}

const STEPS = [
    { bg: 'welcome-1.jpg', hero: true, icons: [['live_tv', 'c1', 'nav.live'], ['movie', 'c2', 'nav.movies'], ['theaters', 'c3', 'nav.series']] },
    { bg: 'welcome-2.jpg', icons: [['live_tv', 'c1', 'nav.live'], ['movie', 'c2', 'nav.movies'], ['theaters', 'c3', 'nav.series'], ['star_border', 'c4', 'welcome.icFavorites'], ['history', 'c5', 'home.continue']] },
    { bg: 'welcome-3.jpg', icons: [['playlist_play', 'c1', 'welcome.icIptv'], ['dns', 'c2', 'welcome.icServers'], ['folder', 'c4', 'welcome.icFolders'], ['hub', 'c5', 'welcome.icHub']] },
    { bg: 'welcome-4.jpg', icons: [['settings_remote', 'c1', 'welcome.icRemote'], ['qr_code_2', 'c2', 'welcome.icQr'], ['tablet_mac', 'c3', 'welcome.icDevices'], ['sync', 'c5', 'welcome.icSync']] },
    { bg: 'welcome-5.jpg', icons: [['check_circle', 'c5', 'welcome.icReady']] }
];

function iconsHtml(list) {
    return `<div class="wl-icons">${list.map(([mi, c, key]) => `<div class="wl-ic ${c}"><span class="mi">${mi}</span><small>${escapeHtml(t(key))}</small></div>`).join('')}</div>`;
}

function slideHtml(i) {
    const s = STEPS[i];
    const n = i + 1;
    if (s.hero) {
        return `<section class="wl-slide is-hero" data-slide="${i}" aria-hidden="${i !== 0}">
            <div class="wl-hero">
                <img class="wl-hero-logo" src="img/intro-full.webp" alt=""/>
                <div class="wl-brand">Filoteca</div>
                <h1 class="wl-tagline"><b>${escapeHtml(t('welcome.1a'))}</b><span>${escapeHtml(t('welcome.1b'))}</span></h1>
                <p class="wl-text">${escapeHtml(t('welcome.1text'))}</p>
                ${iconsHtml(s.icons)}
            </div>
        </section>`;
    }
    return `<section class="wl-slide" data-slide="${i}" aria-hidden="true">
        <div class="wl-body">
            <div class="wl-step">${escapeHtml(t('welcome.step', { n, total: WELCOME_STEPS }))}</div>
            <h1 class="wl-title"><b>${escapeHtml(t('welcome.' + n + 'a'))}</b><span>${escapeHtml(t('welcome.' + n + 'b'))}</span></h1>
            <p class="wl-text">${escapeHtml(t('welcome.' + n + 'text'))}</p>
            ${iconsHtml(s.icons)}
        </div>
    </section>`;
}

export default {
    path: '/welcome/',
    nav: null,

    render() {
        return `<div class="page no-nav" data-name="welcome">
            <div class="page-content wl" data-wl>
                <div class="wl-bg" data-bg style="background-image:url('${bgFor(1)}')"></div>
                <div class="wl-bg is-next" data-bg2></div>
                <div class="wl-scrim"></div>
                <header class="wl-head">
                    <div class="wl-lockup" data-lockup hidden><img src="img/intro-full.webp" alt=""/><span>Filoteca</span></div>
                    <button type="button" class="wl-skip" data-skip>${escapeHtml(t('welcome.skip'))}<span class="mi">chevron_right</span></button>
                </header>
                <div class="wl-slides" data-slides>${STEPS.map((_, i) => slideHtml(i)).join('')}</div>
                <footer class="wl-foot">
                    <button type="button" class="wl-back" data-prev aria-label="${escapeHtml(t('common.back'))}"><span class="mi">chevron_left</span><span class="wl-back-label">${escapeHtml(t('welcome.back'))}</span></button>
                    <div class="wl-dots" data-dots>${STEPS.map((_, i) => `<i class="${i === 0 ? 'is-on' : ''}"></i>`).join('')}</div>
                    <button type="button" class="wl-next" data-next data-nav-default><span data-next-label>${escapeHtml(t('welcome.start'))}</span><span class="mi">chevron_right</span></button>
                </footer>
            </div>
        </div>`;
    },

    mount(page) {
        const root = page.el;
        const app = window.app;
        const preview = profiles.list().length > 0; // desde Ajustes: al terminar se vuelve, no se va a «Añadir proveedor»
        const els = {
            wrap: root.querySelector('[data-wl]'), bg: root.querySelector('[data-bg]'), bg2: root.querySelector('[data-bg2]'),
            slides: Array.from(root.querySelectorAll('.wl-slide')), dots: Array.from(root.querySelectorAll('[data-dots] i')),
            prev: root.querySelector('[data-prev]'), next: root.querySelector('[data-next]'), nextLabel: root.querySelector('[data-next-label]'),
            lockup: root.querySelector('[data-lockup]'), skip: root.querySelector('[data-skip]')
        };
        let index = 0;
        let busy = false;
        const wide = () => typeof matchMedia === 'function' && matchMedia('(min-width: 900px) and (orientation: landscape)').matches;

        const show = (i) => {
            i = Math.max(0, Math.min(STEPS.length - 1, i));
            if (i === index && els.slides[i].classList.contains('is-on')) return;
            const dir = i > index ? 1 : -1;
            const from = els.slides[index];
            const to = els.slides[i];
            els.slides.forEach((s) => { s.classList.remove('is-on', 'is-out-left', 'is-out-right'); s.setAttribute('aria-hidden', 'true'); });
            from.classList.add(dir > 0 ? 'is-out-left' : 'is-out-right');
            to.classList.add('is-on');
            to.setAttribute('aria-hidden', 'false');
            // Fondo: si cambia, se funde con el siguiente.
            const nextBg = `url('${bgFor(i + 1)}')`;
            if (els.bg.style.backgroundImage !== nextBg) {
                els.bg2.style.backgroundImage = nextBg;
                els.bg2.classList.add('is-in');
                setTimeout(() => { els.bg.style.backgroundImage = nextBg; els.bg2.classList.remove('is-in'); }, 520);
            }
            index = i;
            els.dots.forEach((d, k) => d.classList.toggle('is-on', k === i));
            els.wrap.classList.toggle('is-first', i === 0);
            els.wrap.classList.toggle('is-last', i === STEPS.length - 1);
            els.lockup.hidden = i === 0 && !wide(); // en apaisado (tele, ordenador) la marca va arriba a la izquierda desde la primera pantalla
            els.prev.hidden = i === 0;
            els.nextLabel.textContent = i === 0 ? t('welcome.start') : i === STEPS.length - 1 ? t('welcome.enter') : t('welcome.next');
            els.skip.hidden = i === STEPS.length - 1;
        };

        const finish = (skipped) => {
            if (busy) return;
            busy = true;
            markWelcomeDone(true); // terminar o saltar la da por vista: no hace falta preguntar
            const router = app.views.main.router;
            // Desde Ajustes (ya hay fuentes) se vuelve a donde se estaba; si no hay a dónde volver, al Inicio.
            // Las fuentes pueden haber llegado solas mientras se veía (sincronización con otros aparatos): se mira ahora, no al entrar.
            if (preview || profiles.list().length > 0) {
                if (router.history.length > 1) router.back();
                else router.navigate('/', { reloadAll: true, clearPreviousHistory: true });
                return;
            }
            router.navigate(firstScreen({ needsAccount: account.enabled && !account.signedIn }), { reloadAll: true, clearPreviousHistory: true });
        };

        root.addEventListener('click', (e) => {
            if (e.target.closest('[data-skip]')) { e.preventDefault(); finish(true); return; }
            if (e.target.closest('[data-prev]')) { e.preventDefault(); show(index - 1); return; }
            if (e.target.closest('[data-next]')) {
                e.preventDefault();
                if (index === STEPS.length - 1) finish(false); else show(index + 1);
                return;
            }
        });

        // Deslizar con el dedo.
        let sx = null, sy = null;
        root.addEventListener('pointerdown', (e) => { if (e.pointerType === 'mouse' && e.button !== 0) return; sx = e.clientX; sy = e.clientY; }, { passive: true });
        root.addEventListener('pointerup', (e) => {
            if (sx == null) return;
            const dx = e.clientX - sx, dy = e.clientY - sy;
            sx = sy = null;
            if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && !e.target.closest('button')) show(index + (dx < 0 ? 1 : -1));
        }, { passive: true });

        // Flechas (teclado o mando): antes que la navegación general de la tele, que no sabe de pantallas.
        const onKey = (e) => {
            if (!root.classList.contains('page-current') || document.querySelector('.modal-in:not(.toast)')) return;
            if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); show(index + 1); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); show(index - 1); }
        };
        window.addEventListener('keydown', onKey, true);

        // Fondos del servidor: se piden todos al entrar y, al llegar el de la pantalla que se está viendo, se funde sin saltos.
        const swapBg = (n, url) => { if (n === index + 1) { els.bg2.style.backgroundImage = `url('${url}')`; els.bg2.classList.add('is-in'); setTimeout(() => { els.bg.style.backgroundImage = `url('${url}')`; els.bg2.classList.remove('is-in'); }, 520); } };
        for (let n = 1; n <= STEPS.length; n++) preloadRemote(n, swapBg);
        els.slides[0].classList.add('is-on');
        els.slides[0].setAttribute('aria-hidden', 'false');
        els.wrap.classList.add('is-first');
        els.lockup.hidden = !wide();
        els.prev.hidden = true;
        if (device.isTV) root.classList.add('is-tv');
        return () => window.removeEventListener('keydown', onKey, true);
    }
};
